<template>
  <div class="modern-master-view pb-12">
    <!-- Header -->
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-truck</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Órdenes de Entrega</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Registro y control de despachos de mercadería</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        color="success"
        class="font-weight-black text-uppercase tracking-widest rounded-lg px-8 h-44 shadow-sm"
        @click="$router.push({ name: 'CreatePackingSlip', params: { id: -1 } })"
      >
        <v-icon left small>fas fa-plus</v-icon> Nueva Orden
      </v-btn>
    </v-row>

    <!-- Filters -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense align="center">
        <v-col cols="12" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Desde</div>
          <v-text-field
            v-model="filter.created_after"
            outlined dense hide-details clearable type="date"
            class="modern-input"
            @change="() => { options.page = 1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Hasta</div>
          <v-text-field
            v-model="filter.created_before"
            outlined dense hide-details clearable type="date"
            class="modern-input"
            @change="() => { options.page = 1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Estado</div>
          <v-select
            v-model="filter.status"
            :items="[{ value: 'pending', text: 'Pendiente' }, { value: 'delivered', text: 'Entregada' }, { value: 'cancelled', text: 'Anulada' }]"
            item-text="text"
            item-value="value"
            placeholder="Todos"
            outlined dense hide-details clearable
            class="modern-input"
            @change="() => { options.page = 1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Vendedor</div>
          <v-select
            v-model="filter.seller"
            :items="sellers"
            item-text="email"
            item-value="id"
            placeholder="Todos"
            outlined dense hide-details clearable
            class="modern-input"
            @change="() => { options.page = 1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="auto" class="d-flex align-end pb-1 ml-auto">
          <v-btn color="primary" icon class="grey lighten-4" @click="fetch" :loading="loading">
            <v-icon small>fas fa-sync</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Table -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="headers"
        :items="slips"
        :options.sync="options"
        :server-items-length="totalSlips"
        :loading="loading"
        class="modern-table"
        :item-class="item => item.status === 'cancelled' ? 'row-style-cancelled' : ''"
        @update:options="fetch"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
      >
        <template v-slot:top>
          <div class="px-6 py-4 border-bottom bg-lightgrey d-flex align-center">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Total: {{ totalSlips }}</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              @keyup="searchTimeOut"
              placeholder="Buscar por folio, RUT, cliente o patente..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details clearable
              :disabled="loading"
              class="modern-search-field"
            ></v-text-field>
          </div>
        </template>

        <template v-slot:item.id="{ item }">
          <div class="d-flex flex-column py-2">
            <span class="body-2 font-weight-black primary--text lh-1">#{{ item.id }}</span>
            <span v-if="item.document_id" class="caption grey--text">Folio: {{ item.document_id }}</span>
          </div>
        </template>

        <template v-slot:item.date_emited="{ item }">
          <span class="body-2 grey--text text--darken-3 font-weight-medium">{{ item.date_emited | moment('DD/MM/YYYY') }}</span>
        </template>

        <template v-slot:item.rznSocRecep="{ item }">
          <div class="d-flex flex-column py-2">
            <span class="body-2 font-weight-bold grey--text text--darken-4 lh-1-2 mb-1">{{ item.rznSocRecep }}</span>
            <span class="caption grey--text font-weight-black opacity-60">{{ item.rutRecep }}</span>
          </div>
        </template>

        <template v-slot:item.work_order="{ item }">
          <v-chip v-if="item.work_order" x-small outlined color="primary" class="font-weight-black">OT #{{ item.work_order }}</v-chip>
          <span v-else class="caption grey--text opacity-40">-</span>
        </template>

        <template v-slot:item.status="{ item }">
          <div class="d-flex flex-column py-1">
            <v-chip x-small :color="statusColor(item.status) + ' lighten-5'" :class="statusColor(item.status) + '--text font-weight-black'" label>
              {{ item.get_status_display }}
            </v-chip>
            <span v-if="item.status === 'delivered' && item.signed_by" class="caption grey--text mt-1" style="font-size:0.72rem!important">
              <v-icon x-small color="grey lighten-1">fas fa-signature</v-icon> {{ item.signed_by }}
            </span>
            <span v-if="item.status === 'delivered' && item.delivered_at" class="caption grey--text" style="font-size:0.70rem!important;opacity:0.6">
              {{ item.delivered_at | moment('DD/MM/YY HH:mm') }}
            </span>
          </div>
        </template>

        <template v-slot:item.vehicle_plate="{ item }">
          <div v-if="item.vehicle_plate" class="d-flex flex-column">
            <span class="caption font-weight-bold">{{ item.vehicle_plate }}</span>
            <span class="caption grey--text opacity-60">{{ item.driver_name }}</span>
          </div>
          <span v-else class="caption grey--text opacity-40">-</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-menu bottom left offset-y transition="slide-y-transition" content-class="modern-card">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" class="grey lighten-4" @click.stop>
                <v-icon small color="grey darken-2">fas fa-ellipsis-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense class="py-1">
              <v-list-item @click.stop="$router.push({ name: 'CreatePackingSlip', params: { id: item.id } })">
                <v-list-item-icon class="mr-2">
                  <v-icon x-small color="primary">fas fa-edit</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="caption font-weight-bold">Ver / Editar</v-list-item-title>
              </v-list-item>

              <v-list-item :disabled="item.status !== 'pending'" @click.stop="openDeliveredDialog(item)">
                <v-list-item-icon class="mr-2">
                  <v-icon x-small color="success">fas fa-check-circle</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="caption font-weight-bold">Marcar Entregada</v-list-item-title>
              </v-list-item>

              <v-list-item :disabled="item.status === 'cancelled'" @click.stop="printPDF(item)">
                <v-list-item-icon class="mr-2">
                  <v-icon x-small color="grey darken-2">fas fa-print</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="caption font-weight-bold">Generar PDF</v-list-item-title>
              </v-list-item>

              <v-divider class="my-1"></v-divider>

              <v-list-item :disabled="item.status === 'cancelled'" @click.stop="openCancelDialog(item)" class="error--text">
                <v-list-item-icon class="mr-2">
                  <v-icon x-small color="error">fas fa-ban</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="caption font-weight-bold">Anular</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialog: Marcar Entregada -->
    <v-dialog v-model="showDeliveredDialog" max-width="480" persistent>
      <v-card class="modern-card pa-8">
        <div class="text-h6 font-weight-black primary--text mb-6">Registrar Entrega</div>
        <div class="caption grey--text font-weight-black mb-1 ml-1">Nombre del receptor (quien firma)</div>
        <v-text-field
          v-model="signedBy"
          placeholder="Nombre completo del receptor..."
          outlined dense
          class="modern-input mb-4"
        />
        <v-row>
          <v-col>
            <v-btn block outlined color="grey" @click="showDeliveredDialog = false">Cancelar</v-btn>
          </v-col>
          <v-col>
            <v-btn block color="success" class="font-weight-black" :loading="actionLoading" @click="confirmDelivered">
              <v-icon left small>fas fa-check</v-icon> Confirmar
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>

    <!-- Dialog: Anular -->
    <confirm
      v-if="selectedSlip"
      v-model="showCancelDialog"
      :message="`¿Confirma la anulación de la Orden #${selectedSlip.id}?`"
      @ok="confirmCancel"
      @cancel="showCancelDialog = false"
    />
  </div>
</template>

<script>
import { fetchPackingSlips, markPackingSlipDelivered, updatePackingSlip } from '@/api/salesNotes'
import { fetchSellers } from '@/api/auth'
import { pdfPackingSlip } from '@/doc_generators/packingSlip'
import notifications from '@/utils/notifications'
import moment from 'moment'

export default {
  name: 'PackingSlips',
  data() {
    return {
      loading: false,
      actionLoading: false,
      slips: [],
      sellers: [],
      totalSlips: 0,
      options: {},
      search: '',
      timer: null,
      selectedSlip: null,
      signedBy: '',
      showDeliveredDialog: false,
      showCancelDialog: false,
      filter: {
        created_after: moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('YYYY-MM-DD'),
        created_before: '',
        status: '',
        seller: '',
      },
      headers: [
        { text: 'Nro.', align: 'start', value: 'id', class: 'table-header', width: '100px' },
        { text: 'Fecha', align: 'start', value: 'date_emited', class: 'table-header', width: '120px' },
        { text: 'Razón Social / Cliente', align: 'start', value: 'rznSocRecep', class: 'table-header' },
        { text: 'OT', align: 'start', value: 'work_order', class: 'table-header', width: '110px' },
        { text: 'Estado', align: 'start', value: 'status', class: 'table-header', width: '130px' },
        { text: 'Transporte', align: 'start', value: 'vehicle_plate', class: 'table-header', width: '150px' },
        { text: '', align: 'end', value: 'actions', class: 'table-header', sortable: false, width: '60px' },
      ],
    }
  },
  methods: {
    statusColor(status) {
      return { pending: 'warning', delivered: 'success', cancelled: 'error' }[status] || 'grey'
    },
    async fetch() {
      this.loading = true
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      const sort = sortBy && sortBy.length ? sortBy[0] : '-created_at'
      const ordering = sortDesc && sortDesc[0] ? sort : '-' + sort

      const query = [
        `ordering=${ordering}`,
        `page=${page || 1}`,
        `page_size=${itemsPerPage || 20}`,
        `search=${this.search}`,
        `status=${this.filter.status || ''}`,
        `emited_after=${this.filter.created_after || ''}`,
        `emited_before=${this.filter.created_before || ''}`,
        `seller=${this.filter.seller || ''}`,
      ].join('&')

      try {
        const res = await fetchPackingSlips(query)
        this.totalSlips = res.count
        this.slips = res.results
      } catch (e) {
        notifications.showError('Error cargando órdenes de entrega')
      } finally {
        this.loading = false
      }
    },
    searchTimeOut() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.options.page = 1
        this.fetch()
      }, 800)
    },
    openDeliveredDialog(item) {
      this.selectedSlip = item
      this.signedBy = ''
      this.showDeliveredDialog = true
    },
    async confirmDelivered() {
      this.actionLoading = true
      try {
        await markPackingSlipDelivered(this.selectedSlip.id, this.signedBy)
        notifications.showSuccess('Orden marcada como entregada')
        this.showDeliveredDialog = false
        this.fetch()
      } catch (e) {
        notifications.showError('Error al registrar la entrega')
      } finally {
        this.actionLoading = false
      }
    },
    openCancelDialog(item) {
      this.selectedSlip = item
      this.showCancelDialog = true
    },
    async confirmCancel() {
      try {
        await updatePackingSlip({ id: this.selectedSlip.id, status: 'cancelled' })
        notifications.showSuccess(`Orden #${this.selectedSlip.id} anulada`)
        this.showCancelDialog = false
        this.fetch()
      } catch (e) {
        notifications.showError('Error al anular la orden')
      }
    },
    printPDF(item) {
      pdfPackingSlip(item)
    },
  },
  async mounted() {
    this.fetch()
    this.sellers = await fetchSellers()
  },
}
</script>

<style scoped>
.modern-master-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.gap-3 { gap: 12px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.8px; }
.h-44 { height: 44px !important; }
.opacity-40 { opacity: 0.4; }
.opacity-60 { opacity: 0.6; }
.modern-input >>> fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-search-field { width: 340px; }
.modern-search-field >>> fieldset { border-radius: 24px !important; border-color: #e0e4e8 !important; }
.modern-table >>> thead th { background-color: #f8fbfc !important; height: 56px !important; text-transform: uppercase; font-size: 0.75rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; }
.modern-table >>> td { height: 72px !important; border-bottom: 1px solid #f0f3f5 !important; }
.modern-table >>> tr:hover { background-color: #f4f7f9 !important; cursor: pointer; }
.row-style-cancelled { opacity: 0.5; }
</style>
